import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Grid,
  Label,
  Pagination,
  Segment,
  Select,
  Statistic,
  Table,
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API, isAdmin, showError, timestamp2string } from '../../helpers';
import { ITEMS_PER_PAGE } from '../../constants';

const AigcBillContent = () => {
  const { t } = useTranslation();
  const admin = isAdmin();

  const BILL_TYPE_OPTIONS = [
    { key: 'all', text: t('bill.filter.all'), value: '' },
    { key: 'B', text: t('bill.filter.type_b_customer'), value: 'B' },
    { key: 'A', text: t('bill.filter.type_a_cost'), value: 'A' },
  ];
  const [bills, setBills] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);

  const renderBillType = (billType) => {
    if (billType === 'B') {
      return <Label basic color='green'>{t('bill.type.customer')}</Label>;
    } else if (billType === 'A') {
      return <Label basic color='orange'>{t('bill.type.cost')}</Label>;
    }
    return <Label basic>{billType}</Label>;
  };

  const renderOperation = (op) => {
    if (op === 'deduct') return <Label basic color='red'>{t('bill.operation.deduct')}</Label>;
    if (op === 'refund') return <Label basic color='teal'>{t('bill.operation.refund')}</Label>;
    if (op === 'adjust_charge') return <Label basic color='orange'>{t('bill.operation.adjust_charge')}</Label>;
    if (op === 'adjust_refund') return <Label basic color='teal'>{t('bill.operation.adjust_refund')}</Label>;
    return <Label basic>{op}</Label>;
  };

  const [filters, setFilters] = useState({
    bill_type: '',
    model_name: '',
    start_timestamp: Math.floor(new Date(new Date().setDate(new Date().getDate() - 30)).getTime() / 1000),
    end_timestamp: Math.floor(Date.now() / 1000),
  });

  const loadStats = async () => {
    try {
      const url = admin
        ? `/api/aigc/bills/stats?start_timestamp=${filters.start_timestamp}&end_timestamp=${filters.end_timestamp}`
        : `/api/aigc/my-bills/stats?start_timestamp=${filters.start_timestamp}&end_timestamp=${filters.end_timestamp}`;
      const res = await API.get(url);
      if (res.data.success) {
        setStats(res.data.data);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  const loadBills = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        p: page - 1,
        model_name: filters.model_name,
        start_timestamp: filters.start_timestamp,
        end_timestamp: filters.end_timestamp,
      });
      if (admin) params.append('bill_type', filters.bill_type);
      const apiPath = admin ? '/api/aigc/bills/' : '/api/aigc/my-bills/';
      const res = await API.get(`${apiPath}?${params}`);
      if (res.data.success) {
        setBills(res.data.data || []);
        setTotal(res.data.total || 0);
      } else {
        showError(res.data.message);
      }
    } catch (e) {
      showError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadStats();
    loadBills(1);
  }, []);

  const handleSearch = () => {
    setActivePage(1);
    loadStats();
    loadBills(1);
  };

  const handlePageChange = (e, { activePage }) => {
    setActivePage(activePage);
    loadBills(activePage);
  };

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  return (
    <div className='dashboard-container'>
      {/* 统计卡片 */}
      {stats && (
        <Segment style={{ marginBottom: '1em' }}>
          {admin ? (
            <Grid columns={4} textAlign='center' stackable>
              <Grid.Column>
                <Statistic size='small' color='green'>
                  <Statistic.Value>¥{stats.total_revenue.toFixed(4)}</Statistic.Value>
                  <Statistic.Label>{t('bill.aigc.stats.total_revenue')}</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic size='small' color='orange'>
                  <Statistic.Value>¥{stats.total_cost.toFixed(4)}</Statistic.Value>
                  <Statistic.Label>{t('bill.aigc.stats.total_cost')}</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic size='small' color='blue'>
                  <Statistic.Value>¥{stats.total_profit.toFixed(4)}</Statistic.Value>
                  <Statistic.Label>{t('bill.aigc.stats.gross_profit')}</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic size='small' color='violet'>
                  <Statistic.Value>{stats.task_count}</Statistic.Value>
                  <Statistic.Label>{t('bill.aigc.stats.total_tasks')}</Statistic.Label>
                </Statistic>
              </Grid.Column>
            </Grid>
          ) : (
            <Grid columns={3} textAlign='center' stackable>
              <Grid.Column>
                <Statistic size='small' color='green'>
                  <Statistic.Value>¥{(stats.total_spent || 0).toFixed(4)}</Statistic.Value>
                  <Statistic.Label>{t('bill.aigc.stats.total_spent')}</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic size='small' color='blue'>
                  <Statistic.Value>{stats.bill_count || 0}</Statistic.Value>
                  <Statistic.Label>{t('bill.aigc.stats.bill_count')}</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic size='small' color='violet'>
                  <Statistic.Value>{stats.task_count || 0}</Statistic.Value>
                  <Statistic.Label>{t('bill.aigc.stats.task_count')}</Statistic.Label>
                </Statistic>
              </Grid.Column>
            </Grid>
          )}
        </Segment>
      )}

      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>{t('bill.aigc.title')}</Card.Header>

          {/* 筛选栏 */}
          <Form style={{ marginTop: '1em', marginBottom: '1em' }}>
            <Form.Group widths='equal'>
              {admin && (
                <Form.Field>
                  <label>{t('bill.filter.bill_type')}</label>
                  <Select
                    options={BILL_TYPE_OPTIONS}
                    value={filters.bill_type}
                    onChange={(e, { value }) => setFilters({ ...filters, bill_type: value })}
                  />
                </Form.Field>
              )}
              <Form.Input
                label={t('bill.filter.model_name')}
                placeholder={t('bill.filter.optional')}
                value={filters.model_name}
                onChange={(e) => setFilters({ ...filters, model_name: e.target.value })}
              />
              <Form.Input
                label={t('bill.filter.start_time')}
                type='datetime-local'
                value={new Date(filters.start_timestamp * 1000).toISOString().slice(0, 16)}
                onChange={(e) => setFilters({ ...filters, start_timestamp: Math.floor(new Date(e.target.value).getTime() / 1000) })}
              />
              <Form.Input
                label={t('bill.filter.end_time')}
                type='datetime-local'
                value={new Date(filters.end_timestamp * 1000).toISOString().slice(0, 16)}
                onChange={(e) => setFilters({ ...filters, end_timestamp: Math.floor(new Date(e.target.value).getTime() / 1000) })}
              />
              <Form.Field style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button primary onClick={handleSearch} loading={loading}>{t('bill.filter.search')}</Button>
              </Form.Field>
            </Form.Group>
          </Form>

          {/* 表格 */}
          <Table celled striped sortable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>{t('bill.col.time')}</Table.HeaderCell>
                {admin && <Table.HeaderCell>{t('bill.col.type')}</Table.HeaderCell>}
                <Table.HeaderCell>{t('bill.col.model')}</Table.HeaderCell>
                {admin && <Table.HeaderCell>{t('bill.col.user')}</Table.HeaderCell>}
                <Table.HeaderCell>{t('bill.col.amount_cny')}</Table.HeaderCell>
                <Table.HeaderCell>Quota</Table.HeaderCell>
                <Table.HeaderCell>{t('bill.col.operation')}</Table.HeaderCell>
                <Table.HeaderCell>{t('bill.col.status')}</Table.HeaderCell>
                <Table.HeaderCell>{t('bill.col.task_id')}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {bills.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan='9' textAlign='center'>{t('bill.no_data')}</Table.Cell>
                </Table.Row>
              ) : (
                bills.map((bill) => (
                  <Table.Row key={bill.id}>
                    <Table.Cell>{timestamp2string(bill.created_at)}</Table.Cell>
                    {admin && <Table.Cell>{renderBillType(bill.bill_type)}</Table.Cell>}
                    <Table.Cell><code>{bill.model_name}</code></Table.Cell>
                    {admin && <Table.Cell>{bill.username || bill.user_id}</Table.Cell>}
                    <Table.Cell style={{ fontWeight: 'bold', color: '#21ba45' }}>
                      ¥{bill.amount.toFixed(4)}
                    </Table.Cell>
                    <Table.Cell>{bill.quota}</Table.Cell>
                    <Table.Cell>{renderOperation(bill.operation)}</Table.Cell>
                    <Table.Cell>{bill.status}</Table.Cell>
                    <Table.Cell>
                      <code style={{ fontSize: '11px', color: '#999' }}>
                        {bill.task_id ? bill.task_id.substring(0, 24) + '...' : '-'}
                      </code>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>

          {/* 分页 */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1em' }}>
              <Pagination
                activePage={activePage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                size='small'
              />
            </div>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

export default AigcBillContent;
